import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconSearchComponent } from '../icon-search/icon-search.component';
import { IconEyeComponent } from '../icon-eye/icon-eye.component';
import { IconEyeOffComponent } from '../icon-eye-off/icon-eye-off.component';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputType = 'text' | 'password' | 'email' | 'number' | 'date' | 'search';
export type InputIcon = 'search' | 'user' | 'lock' | 'email' | 'none';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, IconSearchComponent, IconEyeComponent, IconEyeOffComponent],
  templateUrl: './input.component.html',
  styleUrl: './input.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    }
  ]
})
export class InputComponent implements ControlValueAccessor {
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() type: InputType = 'text';
  @Input() placeholder: string = '';
  @Input() label: string = '';
  @Input() size: InputSize = 'md';
  @Input() icon?: InputIcon = 'none';
  @Input() disabled: boolean = false;
  @Input() readonly: boolean = false;
  @Input() autocomplete: string = 'off';
  @Input() error?: string | null = null;
  @Input() reserveErrorSpace: boolean = true;
  @Input() customClass: string = '';
  @Input() inputClass: string = '';
  @Input() ariaLabel?: string;
  @Input() value: string = '';

  @Output() valueChange = new EventEmitter<string>();
  @Output() inputEvent = new EventEmitter<Event>();
  @Output() enterKey = new EventEmitter<KeyboardEvent>();

  showPassword: boolean = false;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  get resolvedType(): string {
    if (this.type === 'password') {
      return this.showPassword ? 'text' : 'password';
    }
    return this.type;
  }

  get hasSearchIcon(): boolean {
    return this.icon === 'search' || this.type === 'search';
  }

  get hasLeftIcon(): boolean {
    return this.hasSearchIcon || this.icon === 'user' || this.icon === 'lock' || this.icon === 'email';
  }

  get isPassword(): boolean {
    return this.type === 'password';
  }

  get computedInputClasses(): string {
    const base =
      'w-full bg-surface-header/60 border-[1px] border-solid rounded-lg text-stone-800 placeholder:text-stone-400 outline-none transition-[background-color,border-color,box-shadow] focus:bg-white disabled:opacity-50 disabled:cursor-not-allowed';

    const borderClass = this.error
      ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-500/15'
      : 'border-brand-border hover:border-primary-200 focus:border-primary-300 focus:shadow-focus';

    let sizeClass = 'py-2 text-sm';
    if (this.size === 'sm') {
      sizeClass = 'py-1.5 text-xs';
    } else if (this.size === 'lg') {
      sizeClass = 'py-2.5 text-base';
    }

    const leftPadding = this.hasLeftIcon ? 'pl-10' : 'pl-3.5';
    const rightPadding = this.isPassword ? 'pr-10' : 'pr-3.5';

    return `${base} ${borderClass} ${sizeClass} ${leftPadding} ${rightPadding} ${this.inputClass}`.trim();
  }

  writeValue(val: any): void {
    this.value = val ?? '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    this.inputEvent.emit(event);
  }

  onBlur(): void {
    this.onTouched();
  }

  onKeydownEnter(event: Event): void {
    this.enterKey.emit(event as KeyboardEvent);
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
